<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHTTP } from '@/plugins/http'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import { useContext } from '@/plugins/context'
import { ElMessageBox } from 'element-plus'
import DiscountCode from '@/models/sales/DiscountCode'
import Location from '@/models/company/Location'

const http = useHTTP()
const alertModal = useAlertModal()
const ctx = useContext()

const { t } = useI18n({
  messages: {
    en: {
      discountCodes: 'Discount Codes',
      newCode: 'New',
      code: 'Code',
      store: 'Store',
      status: 'Status',
      addedBy: 'Added by',
      usedBy: 'Used by',
      usedAt: 'Used at',
      del: 'Delete',
      used: 'Used',
      notUsed: 'Not Used',
      all: 'All stores',
      stores: 'Stores',
      multipleSelectionHint: 'Multiple selection is accepted',
      ok: 'OK',
      cancel: 'Cancel',
      empty: 'No discount codes yet.',
      created: 'Discount code created',
      deleted: 'Discount code deleted',
      confirmDeleteTitle: 'Delete discount code',
      confirmDeleteBody: 'Are you sure you want to delete this discount code?'
    },
    ar: {
      discountCodes: 'شفرات الخصم',
      newCode: 'جديد',
      code: 'الشفرة',
      store: 'المتجر',
      status: 'الحالة',
      addedBy: 'أضيف بواسطة',
      usedBy: 'استُخدم بواسطة',
      usedAt: 'استُخدم في',
      del: 'حذف',
      used: 'مستخدم',
      notUsed: 'غير مستخدم',
      all: 'كل المتاجر',
      stores: 'المتاجر',
      multipleSelectionHint: 'يمكن اختيار أكثر من متجر',
      ok: 'موافق',
      cancel: 'إلغاء',
      empty: 'لا توجد شفرات خصم بعد.',
      created: 'تم إنشاء شفرة الخصم',
      deleted: 'تم حذف شفرة الخصم',
      confirmDeleteTitle: 'حذف شفرة الخصم',
      confirmDeleteBody: 'هل أنت متأكد من حذف شفرة الخصم هذه؟'
    }
  }
})

const loading = ref(false)
const codes = ref<DiscountCode[]>([])
const locations = ref<Location[]>([])

function locationName(loc: Location): string {
  return ctx.locale === 'ar' ? loc.arName || loc.enName : loc.enName || loc.arName
}

const locationNameByID = computed<Record<string, string>>(() => {
  const map: Record<string, string> = {}
  for (const loc of locations.value) {
    if (loc.id) map[loc.id] = locationName(loc)
  }
  return map
})

function storeLabel(dc: DiscountCode): string {
  if (dc.appliesToAll) return t('all')
  return (dc.locationIDs || []).map((id) => locationNameByID.value[id] || id).join(', ')
}

// The store the code was actually used in (only once used).
function usedAtLabel(dc: DiscountCode): string {
  if (!dc.isUsed || !dc.usedAtLocationID) return '—'
  return locationNameByID.value[dc.usedAtLocationID] || dc.usedAtLocationID
}

async function load() {
  loading.value = true
  try {
    const [codesRes, locationsRes] = await Promise.all([
      http.get<DiscountCode[]>(DiscountCode.ENDPOINT),
      http.get<Location[]>(Location.ENDPOINT)
    ])
    codes.value = codesRes.map((c) => DiscountCode.from(c))
    locations.value = locationsRes.map((l) => Location.from(l))
  } catch (err) {
    alertModal.showDanger({ title: (err as { title?: string })?.title ?? 'Error' })
  } finally {
    loading.value = false
  }
}

onMounted(load)

// --- New code dialog ---
const dialogOpen = ref(false)
const appliesToAll = ref(true)
const selectedLocationIDs = ref<string[]>([])
const saving = ref(false)

function openDialog() {
  appliesToAll.value = true
  selectedLocationIDs.value = []
  dialogOpen.value = true
}

async function create() {
  saving.value = true
  try {
    await http.post(DiscountCode.ENDPOINT, {
      appliesToAll: appliesToAll.value,
      locationIDs: appliesToAll.value ? [] : selectedLocationIDs.value
    })
    alertModal.showSuccess({ title: t('created') })
    dialogOpen.value = false
    await load()
  } catch (err) {
    alertModal.showDanger({ title: (err as { title?: string; body?: string })?.body ?? (err as { title?: string })?.title ?? 'Error' })
  } finally {
    saving.value = false
  }
}

async function remove(dc: DiscountCode) {
  try {
    await ElMessageBox.confirm(t('confirmDeleteBody'), t('confirmDeleteTitle'), {
      confirmButtonText: t('ok'),
      cancelButtonText: t('cancel'),
      type: 'warning'
    })
  } catch {
    return // user cancelled
  }
  try {
    await http.delete(`${DiscountCode.ENDPOINT}/${dc.id}`)
    alertModal.showSuccess({ title: t('deleted') })
    await load()
  } catch (err) {
    alertModal.showDanger({ title: (err as { title?: string })?.title ?? 'Error' })
  }
}
</script>

<template>
  <div class="p-6" :dir="ctx.getDir()">
    <div class="flex items-center justify-between mb-4">
      <h1 class="text-xl font-bold text-navy">{{ t('discountCodes') }}</h1>
      <el-button type="primary" class="bg-primary" @click="openDialog">{{ t('newCode') }}</el-button>
    </div>

    <el-table :data="codes" v-loading="loading" class="w-full" :empty-text="t('empty')">
      <el-table-column :label="t('code')" min-width="140">
        <template #default="{ row }">
          <span class="font-mono font-semibold">{{ row.formatted }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="t('store')" min-width="180">
        <template #default="{ row }">{{ storeLabel(row) }}</template>
      </el-table-column>
      <el-table-column :label="t('status')" min-width="120">
        <template #default="{ row }">
          <el-tag :type="row.isUsed ? 'success' : 'info'" effect="light">
            {{ row.isUsed ? t('used') : t('notUsed') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column :label="t('addedBy')" min-width="140">
        <template #default="{ row }">{{ row.addedBy?.name ?? '—' }}</template>
      </el-table-column>
      <el-table-column :label="t('usedBy')" min-width="140">
        <template #default="{ row }">{{ row.usedBy?.name ?? '—' }}</template>
      </el-table-column>
      <el-table-column :label="t('usedAt')" min-width="160">
        <template #default="{ row }">{{ usedAtLabel(row) }}</template>
      </el-table-column>
      <el-table-column :label="t('del')" width="100" align="center">
        <template #default="{ row }">
          <el-button v-if="!row.isUsed" type="danger" link @click="remove(row)">
            {{ t('del') }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog v-model="dialogOpen" :title="t('newCode')" width="420px">
      <div class="flex flex-col gap-3" :dir="ctx.getDir()">
        <el-checkbox v-model="appliesToAll" class="self-start">{{ t('all') }}</el-checkbox>
        <div v-if="!appliesToAll">
          <label class="block text-sm text-gray-600 mb-1">{{ t('stores') }}</label>
          <el-select
            v-model="selectedLocationIDs"
            multiple
            filterable
            class="w-full"
            :placeholder="t('stores')"
          >
            <el-option
              v-for="loc in locations"
              :key="loc.id"
              :label="locationName(loc)"
              :value="loc.id"
            />
          </el-select>
          <p class="text-xs text-gray-400 mt-1">{{ t('multipleSelectionHint') }}</p>
        </div>
      </div>
      <template #footer>
        <el-button @click="dialogOpen = false">{{ t('cancel') }}</el-button>
        <el-button
          type="primary"
          class="bg-primary"
          :loading="saving"
          :disabled="!appliesToAll && selectedLocationIDs.length === 0"
          @click="create"
          >{{ t('ok') }}</el-button
        >
      </template>
    </el-dialog>
  </div>
</template>
